<div class="closest-interventions-list">
  <!-- Header -->
  @if (showHeader) {
    <div class="list-header">
      <div class="flex items-center gap-2">
        <h3 class="text-sm font-medium m-0">Interventions</h3>
        @if (loadingBatchIndicators) {
          <span class="loading-badge">
            <i class="pi pi-spin pi-spinner text-[10px]"></i>
            <span>Calcul...</span>
          </span>
        } @else if (interventions.length > 0) {
          <p-badge
            [value]="interventions.length.toString()"
            severity="info"
            styleClass="text-[10px]"
          ></p-badge>
        }
      </div>

      <!-- Controls -->
      @if (showControls) {
        <div class="flex items-center gap-1">
          <button
            class="ctrl-btn"
            [class.animate-spin]="
              loadingInterventions || loadingBatchIndicators
            "
            [disabled]="loadingInterventions || loadingBatchIndicators"
            (click)="onRefresh()"
            title="Recalculer"
            aria-label="Recalculer les interventions"
          >
            <i class="pi pi-refresh"></i>
          </button>

          <button
            class="ctrl-btn"
            (click)="controlsPopover.toggle($event)"
            title="Options d'affichage"
            aria-label="Options d'affichage"
          >
            <i class="pi pi-cog"></i>
          </button>

          <p-popover #controlsPopover>
            <div class="p-3 flex flex-col gap-3 min-w-[160px]">
              <div class="text-sm text-neutral-400 font-medium">Trier par</div>
              <div
                class="flex items-center bg-neutral-800 rounded overflow-hidden border border-neutral-700"
              >
                <button
                  class="sort-btn"
                  [class.sort-active]="sortBy === 'distance'"
                  (click)="onSortByChange('distance')"
                  aria-label="Trier par distance"
                >
                  <i class="pi pi-map-marker"></i> Distance
                </button>
                <button
                  class="sort-btn border-l border-neutral-700"
                  [class.sort-active]="sortBy === 'date'"
                  (click)="onSortByChange('date')"
                  aria-label="Trier par date"
                >
                  <i class="pi pi-calendar"></i> Date
                </button>
              </div>

              <div class="text-sm text-neutral-400 font-medium">Nombre max</div>
              <input
                type="number"
                class="p-1.5 w-full text-center text-sm bg-neutral-800 border border-neutral-700 rounded"
                [ngModel]="limit"
                (ngModelChange)="onLimitChange($event)"
                min="1"
                max="15"
                aria-label="Nombre maximum d'interventions"
              />
            </div>
          </p-popover>
        </div>
      }
    </div>
  }

  <!-- Interventions List -->
  <div class="interventions-scroll">
    @if (loadingInterventions) {
      @for (i of [1, 2, 3, 4, 5]; track i) {
        <div class="intervention-card">
          <div class="flex justify-between items-center">
            <div class="flex-1">
              <p-skeleton
                width="60%"
                height="14px"
                styleClass="mb-1"
              ></p-skeleton>
              <div class="flex items-center gap-2">
                <p-skeleton width="80px" height="12px"></p-skeleton>
                <p-skeleton width="50px" height="12px"></p-skeleton>
              </div>
            </div>
            <p-skeleton
              width="44px"
              height="36px"
              borderRadius="6px"
            ></p-skeleton>
          </div>
          <div class="flex mt-1 gap-1">
            <p-skeleton shape="circle" width="20px" height="20px"></p-skeleton>
            <p-skeleton shape="circle" width="20px" height="20px"></p-skeleton>
          </div>
        </div>
      }
    }

    @for (
      intervention of interventions;
      track trackByInterventionId($index, intervention)
    ) {
      <div
        class="intervention-card"
        [class.intervention-selected]="
          selectedIntervention?.id === intervention.id
        "
        (mouseenter)="
          hoveredInterventionId = intervention.id; onMouseEnter(intervention)
        "
        (mouseleave)="hoveredInterventionId = null; onMouseLeave()"
        (click)="onInterventionClick(intervention)"
        [attr.data-intervention-id]="intervention.id"
      >
        <div class="flex justify-between items-center gap-2">
          <div class="flex-1 min-w-0">
            <p
              class="text-sm font-medium mb-0 truncate"
              [title]="intervention.name"
            >
              {{ intervention.name }}
            </p>
            <div
              class="flex items-center text-xs text-neutral-400 flex-wrap gap-x-1"
            >
              <span class="inline-flex items-center gap-0.5">
                <fa-icon [icon]="faLocationDot" class="text-[10px]"></fa-icon>
                {{
                  intervention.affair?.workSiteLocality ||
                    intervention.workSiteLocality
                }}
              </span>
              <span class="text-neutral-600">&middot;</span>
              <span
                class="inline-flex items-center gap-0.5"
                [title]="
                  intervention.displayDistanceType === 'tablot'
                    ? 'Distance jusqu’à l’affaire Tablot la plus proche'
                    : intervention.distance_type === 'route'
                      ? 'Distance routière'
                      : 'Distance à vol d\'oiseau'
                "
              >
                <fa-icon [icon]="faRoute" class="text-[10px]"></fa-icon>
                <b>{{ intervention.displayDistance ?? intervention.distance | number: '1.1-1' }}</b
                >&nbsp;km
              </span>
              @if (
                intervention.duration_min &&
                (hoveredInterventionId === intervention.id ||
                  selectedIntervention?.id === intervention.id)
              ) {
                <span class="text-neutral-600">&middot;</span>
                <span class="inline-flex items-center gap-0.5">
                  <i class="pi pi-clock text-[10px]"></i>
                  <b>{{ intervention.duration_min }}</b
                  >&nbsp;min
                </span>
              }
            </div>
          </div>

          <!-- Date badge + indicator -->
          <div class="date-badge">
            <span class="text-xs font-bold">{{
              intervention.startDate | date: 'dd/MM'
            }}</span>
            <span class="text-xs text-neutral-400">{{
              intervention.startDate | date: 'HH:mm'
            }}</span>
            @if (
              selectedIntervention?.id === intervention.id &&
              loadingSlotTravelInfo &&
              !getInterventionSlotIndicator(intervention.id)?.calculated
            ) {
              <span
                class="indicator indicator-loading"
                title="Analyse des créneaux en cours..."
              >
                <i class="pi pi-spin pi-spinner text-[10px]"></i>
              </span>
            } @else if (
              loadingBatchIndicators &&
              !getInterventionSlotIndicator(intervention.id)?.calculated
            ) {
              <p-skeleton
                width="28px"
                height="14px"
                styleClass="mt-0.5"
              ></p-skeleton>
            } @else if (
              getInterventionSlotIndicator(intervention.id)?.calculated
            ) {
              @if (
                getInterventionSlotIndicator(intervention.id)!.feasibleCount > 0
              ) {
                <span
                  class="indicator indicator-ok"
                  [title]="
                    getInterventionSlotIndicator(intervention.id)!
                      .feasibleCount + ' créneaux réalisables'
                  "
                >
                  {{
                    getInterventionSlotIndicator(intervention.id)!.feasibleCount
                  }}
                </span>
              } @else if (
                getInterventionSlotIndicator(intervention.id)!.shiftCount > 0
              ) {
                <span
                  class="indicator indicator-shift"
                  [title]="
                    getInterventionSlotIndicator(intervention.id)!.shiftCount +
                    ' créneaux à décaler'
                  "
                >
                  {{
                    getInterventionSlotIndicator(intervention.id)!.shiftCount
                  }}
                </span>
              } @else if (
                getInterventionSlotIndicator(intervention.id)!.adjustableCount >
                0
              ) {
                <span
                  class="indicator indicator-warn"
                  [title]="
                    getInterventionSlotIndicator(intervention.id)!
                      .adjustableCount + ' créneaux avec ajustement'
                  "
                >
                  {{
                    getInterventionSlotIndicator(intervention.id)!
                      .adjustableCount
                  }}
                </span>
              } @else {
                <span
                  class="indicator indicator-none"
                  title="Aucun créneau réalisable"
                  >0</span
                >
              }
            }
          </div>
        </div>

        <!-- Technician badges - toujours visibles -->
        @if (intervention.usersTechnicians?.length) {
          <div class="flex mt-1 gap-1 flex-wrap">
            @for (
              technician of intervention.usersTechnicians;
              track trackByTechnicianId($index, technician)
            ) {
              <div
                class="tech-badge"
                [style.background-color]="technician.color || '#666'"
                [title]="technician.firstname + ' ' + technician.lastname"
              >
                {{ getTechnicianInitials(technician) }}
              </div>
            }
          </div>
        }
      </div>
    } @empty {
      <div class="empty-state">
        <i class="pi pi-info-circle text-neutral-500"></i>
        <span class="text-sm text-neutral-400"
          >Aucune intervention à proximité</span
        >
      </div>
    }
  </div>
</div>
